<template>
  <div>
    <div v-if="!showPagerTable">
      <v-data-table
        class="mt-8"
        data-private
        disable-sort
        :footer-props="footerOptions"
        :headers="headers"
        :items="items"
        :loading="isLoading"
      >
        <template v-slot:item.name="{ item }">
          <span :title="item.name">{{ item.name }}</span>
        </template>

        <template v-slot:item.createdAt="{ item }">
          <span :title="item.createdAt">{{ formatDate(item.createdAt) }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon @click="toggleDeleteConfirmationModal(item)">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </div>

    <div v-else>
      <v-data-table
        class="mt-8"
        data-private
        disable-sort
        :footer-props="footerOptions"
        :headers="headers"
        :items="items"
        :options.sync="options"
        :page="currentPage"
        :server-items-length="totalItems"
        :loading="isLoading"
      >
        <template v-slot:item.name="{ item }">
          <span :title="item.name">{{ item.name }}</span>
        </template>

        <template v-slot:item.createdAt="{ item }">
          <span :title="item.createdAt">{{ formatDate(item.createdAt) }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon @click="toggleDeleteConfirmationModal(item)">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </div>

    <div class="mt-6 text-right">
      <Button
        color="primary"
        size="large"
        :text="$t('common.create_new')"
        @click="isAPIKeysCreateModalVisible = true"
      />
    </div>

    <APIKeysCreateModal
      :show="isAPIKeysCreateModalVisible"
      @cancel="isAPIKeysCreateModalVisible = false"
      @created="onAPIKeyCreated"
    />

    <APIKeysResultModal
      :show="isAPIKeysResultModalVisible"
      :apiKey="apiKey"
      @close="isAPIKeysResultModalVisible = false"
    />

    <DeleteConfirmationModal
      :show="isAPIKeysDeleteConfirmationVisible"
      :isLoading="isDeleting"
      :title="$t('organization_settings.api_key_delete_question')"
      :text="$t('organization_settings.api_key_delete_warning')"
      @cancel="toggleDeleteConfirmationModal"
      @delete="onAPIKeyDeleted"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import moment from 'moment'
import APIKeysCreateModal from './APIKeysCreateModal.vue'
import APIKeysResultModal from './APIKeysResultModal.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'

const INITIAL_ITEMS_PER_PAGE = 20

export default {
  name: 'APIKeysList',

  components: {
    APIKeysCreateModal,
    DeleteConfirmationModal,
    APIKeysResultModal,
    Button,
  },

  props: {
    organizationId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isAPIKeysDeleteConfirmationVisible: false,
      isAPIKeysCreateModalVisible: false,
      isAPIKeysResultModalVisible: false,
      apiKey: null,
      apiKeyToDelete: {},
      isLoading: false,
      items: [],
      headers: [
        {
          text: this.$t('organization_settings.api_key_name'),
          align: 'start',
          sortable: false,
          value: 'name',
        },
        {
          text: this.$t('organization_settings.api_keys_created_at'),
          align: 'start',
          sortable: false,
          value: 'createdAt',
        },
        {
          text: _.capitalize(this.$t('common.action_other')),
          sortable: false,
          align: 'center',
          value: 'actions',
        },
      ],
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: INITIAL_ITEMS_PER_PAGE,
      isDeleting: false,
    }
  },

  created() {
    this.retrieveAPIKeys()
  },

  computed: {
    isPagerEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys.usePagerApiKeys)
    },

    showPagerTable() {
      return this.isPagerEnabled && (this.hasItems || this.isLoading)
    },

    hasItems() {
      return !_.isEmpty(this.items)
    },
    options: {
      get() {
        return {
          page: this.currentPage,
          itemsPerPage: this.itemsPerPage,
        }
      },

      set(event) {
        if (
          event.page === this.currentPage &&
          this.itemsPerPage === event.itemsPerPage
        )
          return
        this.currentPage = event.page
        this.itemsPerPage = event.itemsPerPage

        this.retrieveAPIKeys()
      },
    },
  },

  methods: {
    ...mapActions({
      createAPIKey: 'createAPIKey',
      getAPIKeys: 'getAPIKeys',
      deleteAPIKey: 'deleteAPIKey',
    }),

    async retrieveAPIKeys() {
      this.isLoading = true

      try {
        if (this.isPagerEnabled) {
          const response = await this.getAPIKeys({
            organizationId: this.organizationId,
            limit: this.itemsPerPage,
            skip: (this.currentPage - 1) * this.itemsPerPage,
            sort: { by: 'createdAt', order: 'desc' },
          })

          this.totalItems = response?.data?.totalRecordsInData ?? 0
          this.items = response?.data?.results ?? []
        } else {
          const response = await this.getAPIKeys({
            organizationId: this.organizationId,
          })
          this.totalItems = response?.data?.length ?? 0
          this.items =
            response?.data.sort((a, b) =>
              a.createdAt < b.createdAt ? 1 : -1
            ) ?? []
        }
      } finally {
        this.isLoading = false
      }
    },

    async onAPIKeyCreated(payload) {
      this.isLoading = true

      try {
        const response = await this.createAPIKey({
          organizationId: this.organizationId,
          name: payload.name,
          applicationAccount: this.$store.getters.account._id,
        })

        this.apiKey = response.data.APIKey
        // TODO: Handle errors
      } finally {
        this.isLoading = false
        this.isAPIKeysCreateModalVisible = false
        this.isAPIKeysResultModalVisible = true
        this.retrieveAPIKeys()
      }
    },

    toggleDeleteConfirmationModal(apiKey = {}) {
      this.apiKeyToDelete = apiKey
      this.isAPIKeysDeleteConfirmationVisible = !this
        .isAPIKeysDeleteConfirmationVisible
    },

    async onAPIKeyDeleted() {
      try {
        this.isDeleting = true
        await this.deleteAPIKey({
          apiKeyId: this.apiKeyToDelete?.id ?? this.apiKeyToDelete?._id,
          organizationId: this.organizationId,
        })
        this.isAPIKeysDeleteConfirmationVisible = false
        this.retrieveAPIKeys()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
      }
    },

    formatDate(dateString) {
      return moment(dateString).format('LLL')
    },
  },
}
</script>
